import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  User,
  Send,
  CheckCircle,
  AlertTriangle,
  Loader2,
  FileText,
  Shield,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPRINT = {
  owner: "Reto Ramstein",
  company: "Project Radar AR",
  address: "3000 Bern 14",
  po: "Postfach 590",
  country: "Schweiz",
  email: "ramsteinreto@gmail.com",
  phone: "+41 79 463 91 78",
};

export default function ContactPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const defaultRole = user?.role || "";
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    role: defaultRole,
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // "ok" | "err"
  const [errMsg, setErrMsg] = useState("");

  // Prefill when user context becomes available
  useEffect(() => {
    if (user) {
      setForm((p) => ({
        ...p,
        name: p.name || user.name || "",
        email: p.email || user.email || "",
        role: p.role || user.role || "",
      }));
    }
  }, [user]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setErrMsg("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form, { withCredentials: true });
      setStatus("ok");
      setForm((p) => ({ ...p, subject: "", message: "" }));
    } catch (err) {
      setStatus("err");
      setErrMsg(err?.response?.data?.detail || t.contact.error);
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-14"
        >
          <span className="text-xs font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">
            {t.contact.badge}
          </span>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-light tracking-tight text-white leading-tight mt-4">
            {t.contact.title_a} <span className="neon-text font-medium">{t.contact.title_b}</span>
          </h1>
          <p className="text-[#A0A0B0] leading-relaxed mt-4">
            {t.contact.lead}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Impressum Sidebar */}
          <aside className="lg:col-span-5 space-y-6">
            <motion.div
              data-testid="imprint-card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass-surface rounded-xl p-7 relative overflow-hidden"
            >
              <div className="absolute -top-14 -right-14 w-48 h-48 bg-[#00F0FF]/5 rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/25">
                    <FileText className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <div>
                    <span className="text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">
                      {t.contact.imprint_label}
                    </span>
                    <h3 className="font-['Outfit'] text-xl font-medium text-white mt-0.5">
                      {t.contact.imprint_title}
                    </h3>
                  </div>
                </div>

                <ul className="space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <User className="w-4 h-4 text-[#A0A0B0] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-[11px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider block">
                        {t.contact.owner}
                      </span>
                      <span data-testid="imprint-owner" className="text-white font-medium">
                        {IMPRINT.owner}
                      </span>
                      <span className="text-[11px] text-[#A0A0B0] block mt-0.5">
                        {IMPRINT.company}
                      </span>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#A0A0B0] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-[11px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider block">
                        {t.contact.address}
                      </span>
                      <span className="text-white">{IMPRINT.po}</span>
                      <span className="text-white block">{IMPRINT.address}</span>
                      <span className="text-[#A0A0B0] text-[11px] font-['JetBrains_Mono'] block mt-0.5">
                        {IMPRINT.country}
                      </span>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-[#A0A0B0] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-[11px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider block">
                        {t.contact.email}
                      </span>
                      <a
                        data-testid="imprint-email"
                        href={`mailto:${IMPRINT.email}`}
                        className="text-[#00F0FF] hover:text-white transition-colors font-['JetBrains_Mono'] text-[13px] break-all"
                      >
                        {IMPRINT.email}
                      </a>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-[#A0A0B0] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-[11px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider block">
                        {t.contact.phone}
                      </span>
                      <a
                        data-testid="imprint-phone"
                        href={`tel:${IMPRINT.phone.replace(/\s/g, "")}`}
                        className="text-white hover:text-[#00F0FF] transition-colors font-['JetBrains_Mono'] text-[13px]"
                      >
                        {IMPRINT.phone}
                      </a>
                    </div>
                  </li>
                </ul>

                <div className="mt-6 pt-4 border-t border-white/5 flex items-start gap-2">
                  <Shield className="w-3.5 h-3.5 text-[#606070] mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-[#606070] leading-relaxed">
                    {t.contact.legal}
                  </p>
                </div>
              </div>
            </motion.div>
          </aside>

          {/* Contact Form */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="glass-surface rounded-xl p-7 lg:p-9"
            >
              <h2 className="font-['Outfit'] text-xl font-medium text-white mb-1">
                {t.contact.form_title}
              </h2>
              <p className="text-xs text-[#A0A0B0] mb-7">
                {t.contact.form_sub}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] text-[#A0A0B0] font-['JetBrains_Mono'] uppercase tracking-wider block mb-1.5">
                      {t.contact.name} *
                    </label>
                    <input
                      data-testid="contact-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Max Muster"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-2.5 text-sm outline-none rounded-lg transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-[#A0A0B0] font-['JetBrains_Mono'] uppercase tracking-wider block mb-1.5">
                      {t.contact.email} *
                    </label>
                    <input
                      data-testid="contact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="max@example.com"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-2.5 text-sm outline-none rounded-lg font-['JetBrains_Mono'] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] text-[#A0A0B0] font-['JetBrains_Mono'] uppercase tracking-wider block mb-1.5">
                    {t.contact.i_am}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: "client", l: t.roles.client, c: "#00F0FF" },
                      { v: "freelancer", l: t.roles.freelancer, c: "#FFB800" },
                      { v: "other", l: t.roles.other, c: "#A855F7" },
                    ].map((r) => {
                      const active = form.role === r.v;
                      return (
                        <button
                          key={r.v}
                          type="button"
                          data-testid={`contact-role-${r.v}`}
                          onClick={() => update("role", r.v)}
                          className="py-2.5 px-3 rounded-lg text-xs font-['Outfit'] font-medium transition-all border"
                          style={{
                            color: active ? r.c : "#A0A0B0",
                            borderColor: active ? `${r.c}55` : "rgba(255,255,255,0.08)",
                            background: active ? `${r.c}10` : "rgba(255,255,255,0.02)",
                          }}
                        >
                          {r.l}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-[11px] text-[#A0A0B0] font-['JetBrains_Mono'] uppercase tracking-wider block mb-1.5">
                    {t.contact.subject} *
                  </label>
                  <input
                    data-testid="contact-subject"
                    type="text"
                    required
                    value={form.subject}
                    onChange={(e) => update("subject", e.target.value)}
                    placeholder={t.contact.subject_ph}
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-2.5 text-sm outline-none rounded-lg transition-colors"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-[#A0A0B0] font-['JetBrains_Mono'] uppercase tracking-wider block mb-1.5">
                    {t.contact.message} *
                  </label>
                  <textarea
                    data-testid="contact-message"
                    rows={6}
                    required
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder={t.contact.message_ph}
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 text-sm outline-none resize-none rounded-lg transition-colors"
                  />
                </div>

                {status === "ok" && (
                  <motion.div
                    data-testid="contact-success"
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 p-4 rounded-lg bg-[#00F0FF]/5 border border-[#00F0FF]/25"
                  >
                    <CheckCircle className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-sm text-[#00F0FF] font-['Outfit'] font-medium block">
                        {t.contact.success_t}
                      </span>
                      <span className="text-xs text-[#A0A0B0]">
                        {t.contact.success_d}
                      </span>
                    </div>
                  </motion.div>
                )}

                {status === "err" && (
                  <motion.div
                    data-testid="contact-error"
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 p-4 rounded-lg bg-[#FF3B30]/5 border border-[#FF3B30]/25"
                  >
                    <AlertTriangle className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#FF3B30]">{errMsg}</span>
                  </motion.div>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto px-8"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {t.contact.submit}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
